import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Save, Search, Radio } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  listInquiries, getInquiry, setInquiryStatus, saveInquiryNotes, syncInquiry, errText,
} from "../lib/api";
import { FORM_LIST } from "./forms/formConfig";

const STATUSES = ["New", "Reviewing", "Replied", "In Conversation", "Closed"];

const TARGET_LABEL = {
  partner_ecosystem: "Creative Service Provider Applications",
  partner_applications: "Partner Applications",
  leads_intakes: "Leads & Intakes",
  general_inquiries: "General Inquiries",
};

export function InquiryList() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      listInquiries({ status, inquiry_type: type, q: q || undefined }).then(setRows);
    }, 250);
    return () => clearTimeout(t);
  }, [status, type, q]);

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-display text-3xl font-black text-zinc-50">Inquiries</h1>
      <p className="mt-2 text-sm text-zinc-500">Every contact form submission, routed to its Notion database.</p>

      <div className="mt-8 space-y-3">
        <div className="relative md:max-w-sm">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <Input data-testid="inquiry-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, organization, message…"
            className="h-12 rounded-xl border-zinc-800 bg-zinc-900/70 pl-11 text-zinc-100 placeholder:text-zinc-600" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...FORM_LIST.map((f) => f.inquiryType)].map((t) => (
            <button key={t} data-testid={`inquiry-type-${t}`} onClick={() => setType(t)}
              className={`font-mono-tech rounded-full border px-3.5 py-2 text-[10px] transition-colors duration-200 ${
                type === t ? "border-brand-pink bg-brand-pink text-zinc-950" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button key={s} data-testid={`inquiry-filter-${s}`} onClick={() => setStatus(s)}
              className={`font-mono-tech rounded-full border px-3.5 py-2 text-[10px] transition-colors duration-200 ${
                status === s ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 border-t border-zinc-900" data-testid="inquiries-table">
        {rows.length === 0 && <p className="py-10 text-sm text-zinc-500">No enquiries yet.</p>}
        {rows.map((r) => (
          <Link key={r.id} to={`/control/inquiries/${r.id}`} data-testid={`inquiry-row-${r.id}`}
            className="grid grid-cols-1 gap-3 border-b border-zinc-900 px-1 py-5 transition-colors duration-200 hover:bg-zinc-900/50 md:grid-cols-[1.4fr_1.4fr_1fr_auto] md:items-center">
            <div>
              <p className="font-display font-bold text-zinc-50">{r.display_name}</p>
              <p className="font-mono-tech text-[10px] text-zinc-500">{r.contact?.email} · {r.created_at?.slice(0, 10)}</p>
            </div>
            <p className="text-sm text-brand-pink">{r.inquiry_type}</p>
            <p className="font-mono-tech text-[10px] text-brand-indigo">{r.status}</p>
            <div className="flex flex-wrap gap-1.5">
              {r.notion?.synced && (
                <span className="font-mono-tech rounded-sm border border-green-500/40 bg-green-500/10 px-2 py-1 text-[9px] text-green-400">
                  <Radio className="inline h-2.5 w-2.5" /> {TARGET_LABEL[r.target] || r.target}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="grid gap-1 border-b border-zinc-900 py-3 sm:grid-cols-[34%_1fr] sm:gap-4">
    <dt className="text-sm text-zinc-500">{k}</dt>
    <dd className="whitespace-pre-wrap break-words text-sm text-zinc-200">{v || "—"}</dd>
  </div>
);

export function InquiryDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getInquiry(id).then((d) => { setDoc(d); setNotes(d.internal_notes || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!doc) return <p className="font-mono-tech p-10 text-[11px] text-zinc-500">Loading…</p>;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(msg); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const c = doc.contact || {};

  return (
    <div className="p-6 md:p-10">
      <Link to="/control/inquiries" data-testid="back-to-inquiries" className="font-mono-tech flex items-center gap-2 text-[10px] text-zinc-500 hover:text-zinc-200">
        <ArrowLeft className="h-3 w-3" /> All inquiries
      </Link>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-zinc-50" data-testid="inquiry-name">{doc.display_name}</h1>
          <p className="font-mono-tech mt-2 text-[10px] text-zinc-500">
            {doc.inquiry_type} · {c.email} · {doc.created_at?.slice(0, 10)} · → {TARGET_LABEL[doc.target] || doc.target}
          </p>
        </div>
        <Button data-testid="inquiry-sync-btn" disabled={busy} onClick={() => act(() => syncInquiry(id), "Synced to Notion")}
          className="h-11 rounded-full border border-zinc-800 bg-transparent px-5 text-sm text-zinc-200 hover:border-brand-indigo/60">
          <RefreshCw className="mr-2 h-4 w-4" /> Sync to Notion
        </Button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="inquiry-status-controls">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`inquiry-status-${s}`} disabled={busy}
            onClick={() => act(() => setInquiryStatus(id, s), `Status set to ${s}`)}
            className={`font-mono-tech rounded-full border px-4 py-2 text-[10px] transition-colors duration-200 ${
              doc.status === s ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
            {s}
          </button>
        ))}
      </div>

      {doc.notion?.last_error && (
        <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="inquiry-notion-error">
          Notion: {doc.notion.last_error}
        </p>
      )}

      <div className="md:grid md:grid-cols-[1fr_320px] md:gap-10">
        <dl className="mt-10 min-w-0" data-testid="inquiry-detail">
          <Row k="Enquiring about" v={doc.inquiry_type} />
          <Row k="Name" v={c.name} />
          <Row k="Organization" v={c.organization} />
          <Row k="Email" v={c.email} />
          <Row k="Phone" v={c.phone} />
          <Row k="Partner type" v={[...(doc.partner_type || []).filter((t) => t !== "Other"), doc.partner_type_other].filter(Boolean).join(", ")} />
          <Row k="Genres" v={[...(doc.genres || []).filter((g) => g !== "Other"), doc.genre_other].filter(Boolean).join(", ")} />
          <Row k="Help areas" v={(doc.help_areas || []).join("\n")} />
          <Row k="Links" v={(doc.links || []).join("\n")} />
          <Row k="Socials" v={(doc.socials || []).map((s) => `${s.platform}: ${s.url}`).join("\n")} />
          <Row k="Message" v={doc.message} />
          <Row k="Source" v={doc.source} />
          <Row k="Attribution" v={Object.entries(doc.attribution || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join("\n")} />
        </dl>

        <aside className="mt-10">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="inquiry-notes">
            <p className="font-mono-tech text-[10px] text-zinc-500">Internal notes</p>
            <Textarea data-testid="inquiry-notes-input" rows={7} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-3 rounded-xl border-zinc-800 bg-zinc-900/70 text-zinc-100" placeholder="Only the team sees this." />
            <Button data-testid="inquiry-notes-save" disabled={busy} onClick={() => act(() => saveInquiryNotes(id, notes), "Notes saved")}
              className="mt-3 h-11 w-full rounded-full bg-brand-indigo text-sm font-semibold text-white hover:bg-brand-indigo-soft">
              <Save className="mr-2 h-4 w-4" /> Save notes
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
